import {
  Store,
  Reducer,
  Action,
  UnknownAction,
  Middleware
} from 'redux'
import { configureStore } from '@reduxjs/toolkit'
import { createLogger } from 'redux-logger'
import {
  Persistor,
  persistStore,
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  REHYDRATE
} from 'redux-persist'
import createSagaMiddleware, { Saga } from 'redux-saga'

let isLoggerEnabled = false

export function enableReduxLogger() {
  isLoggerEnabled = true
}

export function disableReduxLogger() {
  isLoggerEnabled = false
}

function createReduxLoggerMiddleware() {
  const SAGA_LOGGING_BLACKLIST = [
    'EFFECT_TRIGGERED',
    'EFFECT_RESOLVED',
    'EFFECT_REJECTED',
    'persist/REHYDRATE'
  ]
  const logger = createLogger({
    collapsed: true,
    logger: {
      groupCollapsed: (message: string) => {
        const styles = ['color: #A700E4; font-weight: bold;']

        if (message.indexOf('%c[SAGA]') !== -1) {
          styles.push('color: #7200ff; font-weight: bold;')
        }

        if (message.indexOf('%c[REDUX] %cREHYDRATED') !== -1) {
          styles.push('color: #0083ff; font-weight: bold;')
        } else if (message.indexOf('_SUCCESS') !== -1) {
          styles.push('color: #00ff94; font-weight: bold;')
        } else if (message.indexOf('_FAILURE') !== -1) {
          styles.push('color: #ff5000; font-weight: bold;')
        } else {
          styles.push('font-weight: normal;')
        }
        return console.groupCollapsed(message, ...styles)
      },
      groupEnd: console.groupEnd,
      log: console.log
    },
    predicate: (_getState, { type }) =>
      isLoggerEnabled && !SAGA_LOGGING_BLACKLIST.includes(type),
    titleFormatter: (action: any) => {
      if (action['@@redux-saga/SAGA_ACTION']) {
        return `%c[REDUX]%c[SAGA] %c${action.type}`
      }
      return `%c[REDUX] %c${action.type}`
    }
  })
  return logger
}

interface Context {
  store: Store<any, any> | undefined
  persistor: Persistor | undefined
}

const context: Context = {
  persistor: undefined,
  store: undefined
}

export interface CreateReduxStoreOptions<
  S = any,
  A extends Action = UnknownAction
> {
  reducer: Reducer<S, A>
  saga: Saga
  middlewares?: Middleware[]
  debug?: boolean
  persistorCallback?: (store: Store<S, A>) => any
}

export function createReduxStore<S = any, A extends Action = UnknownAction>({
  reducer,
  saga,
  middlewares,
  debug,
  persistorCallback
}: CreateReduxStoreOptions): {
  store: Store<S, A>
  persistor: Persistor
} {
  const sagaMiddleware = createSagaMiddleware()
  const store = configureStore({
    reducer,
    devTools: debug,
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware({
        thunk: false,
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
          ignoredActionPaths: ['meta.callback']
        }
      }).concat(
        sagaMiddleware,
        ...(middlewares ?? []),
        ...(debug ? [createReduxLoggerMiddleware()] : [])
      )
  }) as unknown as Store<S, A>

  const persistor = persistStore(store, undefined, () => {
    if (persistorCallback) persistorCallback(store)
  })

  sagaMiddleware.run(saga)

  context.store = store
  context.persistor = persistor

  return { store, persistor }
}

export function getReduxStore<S = any, A extends Action = UnknownAction>(): Store<
  S,
  A
> {
  return context.store!
}

export function getReduxPersistor(): Persistor {
  return context.persistor!
}
